// Header.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';
import { getInitials } from '../../utils/helpers';
import './Header.css';

const Header = ({ toggleSidebar, sidebarOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);

  const [notifications] = useState([
    { id: 1, type: 'success', message: 'New product added successfully', time: '5 min ago', read: false },
    { id: 2, type: 'warning', message: 'Low stock alert for Monitor', time: '1 hour ago', read: false },
    { id: 3, type: 'info', message: 'Delivery scheduled for tomorrow', time: '2 hours ago', read: true }
  ]);

  // Search suggestions
  const searchSuggestions = [
    { type: 'product', text: 'Products', icon: 'fas fa-box' },
    { type: 'challan', text: 'Delivery Challans', icon: 'fas fa-truck' },
    { type: 'report', text: 'Reports', icon: 'fas fa-chart-bar' },
    { type: 'user', text: 'Users', icon: 'fas fa-users' }
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchSuggestions(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadNotifications = notifications.filter(n => !n.read).length;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchSuggestions(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSearchSuggestions(e.target.value.length > 0);
  };

  const handleSuggestionClick = (suggestion) => {
    const query = searchQuery.trim();
    if (query) {
      navigate(`/search?q=${encodeURIComponent(query)}&type=${suggestion.type}`);
    } else {
      navigate(`/search?type=${suggestion.type}`);
    }
    setShowSearchSuggestions(false);
  };

  const handleQuickSearch = (type) => {
    navigate(`/search?type=${type}`);
    setShowSearchSuggestions(false);
  };

  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('.search-input');
        if (searchInput) {
          searchInput.focus();
        }
      }
      // Escape key to close suggestions
      if (e.key === 'Escape') {
        setShowSearchSuggestions(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <header className="header">
      <div className="header-left">
        <button
          className={`sidebar-toggle ${sidebarOpen ? 'active' : ''}`}
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <div className="hamburger">
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </div>
        </button>

        <div className="logo">
          <div className="logo-icon">
            <i className="fas fa-boxes"></i>
          </div>
          <div className="logo-text" style={{ textAlign: 'left' }}>
            <h1>IMS</h1>
            <span className="logo-subtitle">Inventory Management System</span>
          </div>
        </div>
      </div>

      <div className="header-center">
        <div className="search-bar" ref={searchRef}>
          <form onSubmit={handleSearchSubmit} className="search-form">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setShowSearchSuggestions(searchQuery.length > 0)}
              placeholder="Search products, orders, reports..."
              className="search-input"
            />
            <button type="button" className="search-shortcut" onClick={() => document.querySelector('.search-input').focus()}>
              <span>⌘ Salkatech IMS</span>
            </button>
          </form>

          {/* Search Suggestions */}
          {showSearchSuggestions && (
            <div className="search-suggestions">
              <div className="suggestions-header">
                <span>Quick Search</span>
                <button
                  className="close-suggestions"
                  onClick={() => setShowSearchSuggestions(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="suggestions-list">
                {searchSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <i className={suggestion.icon}></i>
                    <span>{suggestion.text}</span>
                    <span className="suggestion-shortcut">Tab</span>
                  </div>
                ))}
              </div>
              {searchQuery && (
                <div className="search-action">
                  <button
                    className="search-all-btn"
                    onClick={handleSearchSubmit}
                  >
                    <i className="fas fa-search"></i>
                    Search for "{searchQuery}"
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="header-right">
        {/* Quick Search Actions */}
        <div className="quick-actions">
          <button
            className="action-btn"
            title="Search Products"
            onClick={() => handleQuickSearch('product')}
          >
            <i className="fas fa-box"></i>
          </button>
          <button
            className="action-btn"
            title="Search Challans"
            onClick={() => handleQuickSearch('challan')}
          >
            <i className="fas fa-truck"></i>
          </button>
        </div>

        {/* Notifications
        <div className="notification-wrapper" ref={notificationsRef}>
          <button
            className={`notification-btn ${unreadNotifications > 0 ? 'has-notifications' : ''}`}
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            aria-label="Notifications"
          >
            <i className="fas fa-bell"></i>
            {unreadNotifications > 0 && (
              <span className="notification-badge">{unreadNotifications}</span>
            )}
          </button>

          {isNotificationsOpen && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
                <span className="notification-count">{unreadNotifications} Unread</span>
              </div>

              <div className="notification-list">
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`notification-item ${notification.read ? 'read' : 'unread'} ${notification.type}`}
                  >
                    <div className="notification-icon">
                      <i className={`fas fa-${notification.type === 'success' ? 'check-circle' :
                        notification.type === 'warning' ? 'exclamation-triangle' :
                          'info-circle'
                        }`}></i>
                    </div>
                    <div className="notification-content">
                      <p className="notification-message">{notification.message}</p>
                      <span className="notification-time">{notification.time}</span>
                    </div>
                    {!notification.read && <div className="notification-dot"></div>}
                  </div>
                ))}
              </div>

              <div className="notification-footer">
                <button className="view-all-btn">View All Notifications</button>
              </div>
            </div>
          )}
        </div> */}

        {/* User Profile */}
        <div className="user-profile-wrapper" ref={dropdownRef}>
          <button
            className="user-profile-trigger"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className="user-avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} />
              ) : (
                <span className="avatar-initials">{getInitials(user?.name || 'U')}</span>
              )}
              <div className="user-status"></div>
            </div>

            <div className="user-info">
              <span className="user-name">{user?.name}</span>
              <span className="user-role">{user?.role}</span>
            </div>

            <i className={`fas fa-chevron-down dropdown-arrow ${isDropdownOpen ? 'rotate' : ''}`}></i>
          </button>

          {isDropdownOpen && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <div className="dropdown-avatar">
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.name} />
                  ) : (
                    <span className="avatar-initials-large">{getInitials(user?.name || 'U')}</span>
                  )}
                </div>
                <div className="dropdown-user-info">
                  <h4>{user?.name}</h4>
                  {/* <p>{user?.email}</p> */}
                  {/* <span className="user-role-badge">{user?.role}</span> */}
                </div>
              </div>

              <div className="dropdown-divider"></div>

              <div className="dropdown-menu">
                {/* <a href="/profile" className="dropdown-item">
                  <i className="fas fa-user"></i>
                  <span>My Profile</span>
                </a> */}
                {/* <a href="/settings" className="dropdown-item">
                  <i className="fas fa-cog"></i>
                  <span>Settings</span>
                </a> */}
                <a href="/help-support" className="dropdown-item">
                  <i className="fas fa-question-circle"></i>
                  <span>Help & Support</span>
                </a>
              </div>

              <div className="dropdown-divider"></div>

              <div className="dropdown-menu">
                <button className="dropdown-item logout-btn" onClick={logout}>
                  <i className="fas fa-sign-out-alt"></i>
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;